import React, { useMemo, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import Icon1 from "@assets/icons/Icon";
import { useVehiculesStore } from "@store/vehiculesStore";
import { useDataStore } from "@store/user_org";
import { ChunkyTabs } from "@components/ui/selection/ChunkyTabs";
import SearchField from "@components/ui/textfields/SearchField";
import CheckBox from "@components/ui/selection/CheckBox";
import MiniCalendar from "@components/ui/MiniCalendar";
import type { Vehicle, VehicleReservation } from "../types/garage/vehicules";
import { Text1, Text2 } from "@components/ui/texts/Texts";
import Button from "@components/ui/buttons/Button";
import DayReservationGrid from "./vehicules/DayReservationGrid";
import DataGrid from "@components/ui/tables/DataGrid";
import { transformVehiclesToTable } from "@components/ui/tables/transformToTables";
import VehiculeModal from "@components/modals/VehiculeModal";
import { searchVehiclesFull } from "@services/algolia";

// --- Filter section ---

const CATEGORY_FILTERS: { id: "autre" | "carrosserie" | "mecanique"; label: string }[] = [
  { id: "autre", label: "Autre" },
  { id: "carrosserie", label: "Carrosserie" },
  { id: "mecanique", label: "Mécanique" },
];

const USAGE_FILTERS: { id: "velos" | "location" | "pret"; label: string }[] = [
  { id: "velos", label: "Vélos" },
  { id: "location", label: "Location" },
  { id: "pret", label: "Prêt" },
];

// --- Reservation Grid ---

const VEHICLE_COL_WIDTH = 170;
const ROW_HEIGHT = 32;

function ReservationGrid() {
  const getFilteredVehicles = useVehiculesStore((s) => s.getFilteredVehicles);
  const getWeekDays = useVehiculesStore((s) => s.getWeekDays);
  const reservations = useVehiculesStore((s) => s.reservations);
  const selectedDate = useVehiculesStore((s) => s.selectedDate);
  const highlightedId = useVehiculesStore((s) => s.highlightedReservationId);

  const vehicles = getFilteredVehicles();
  const weekDays = getWeekDays();

  const firstDate = weekDays[0]?.date || "";
  const lastDate = weekDays[weekDays.length - 1]?.date || "";

  // Build a map of reservations per vehicle, filtered to visible range
  const resByVehicle = useMemo(() => {
    const map: Record<string, VehicleReservation[]> = {};
    for (const v of vehicles) map[v.id] = [];
    for (const r of reservations) {
      if (map[r.vehicleId] && r.startDate <= lastDate && r.endDate >= firstDate) {
        map[r.vehicleId].push(r);
      }
    }
    return map;
  }, [vehicles, reservations, firstDate, lastDate]);

  // Compute departure/arrival counts per day
  const dayCounts = useMemo(() => {
    const counts: Record<string, { departures: number; arrivals: number }> = {};
    for (const day of weekDays) {
      let dep = 0;
      let arr = 0;
      for (const r of reservations) {
        if (r.startDate === day.date) dep++;
        if (r.endDate === day.date) arr++;
      }
      counts[day.date] = { departures: dep, arrivals: arr };
    }
    return counts;
  }, [weekDays, reservations]);

  // Today line
  const todayStr = selectedDate; // Use selected date as "today" for demo

  const HOURS_START = 8;
  const HOURS_END = 20;
  const HOURS_SPAN = HOURS_END - HOURS_START; // 12

  const getBarStyle = (r: VehicleReservation) => {
    // Clamp to visible range
    const sDate = r.startDate < firstDate ? firstDate : r.startDate;
    const eDate = r.endDate > lastDate ? lastDate : r.endDate;

    let sIdx = weekDays.findIndex((d) => d.date === sDate);
    if (sIdx < 0) sIdx = 0;

    let eIdx = weekDays.findIndex((d) => d.date === eDate);
    if (eIdx < 0) eIdx = weekDays.length - 1;

    const colCount = weekDays.length;
    const colWidth = 100 / colCount; // one day column in %

    const isSingleDay = r.startDate === r.endDate;

    if (isSingleDay && r.startHour != null && r.durationHours != null) {
      // Single-day: position within the day column based on time
      const hourOffset = (r.startHour - HOURS_START) / HOURS_SPAN;
      const hourWidth = r.durationHours / HOURS_SPAN;
      const left = sIdx * colWidth + hourOffset * colWidth;
      const width = Math.max(0.3 * colWidth, hourWidth * colWidth);
      return { left: `${left}%`, width: `${width}%` };
    }

    // Multi-day: offset start/end columns by startHour/returnHour
    const startOffset = (r.startHour != null && sDate === r.startDate)
      ? (r.startHour - HOURS_START) / HOURS_SPAN
      : 0;
    const endOffset = (r.returnHour != null && eDate === r.endDate)
      ? (r.returnHour - HOURS_START) / HOURS_SPAN
      : 1;

    const left = sIdx * colWidth + startOffset * colWidth;
    const right = eIdx * colWidth + endOffset * colWidth;
    let width = right - left;
    if (width < 0.3 * colWidth) width = 0.3 * colWidth;

    return { left: `${left}%`, width: `${width}%` };
  };

  // Today line position
  const todayIdx = weekDays.findIndex((d) => d.date === todayStr);
  const colCount = weekDays.length;
  const todayPct = todayIdx >= 0 ? ((todayIdx + 0.5) / colCount) * 100 : -1;

  return (
    <div className="flex flex-row" style={{ border: "1px solid #D9D9D9", borderRadius: 10, overflow: "hidden" }}>
      {/* Left: vehicle labels column */}
      <div style={{ width: VEHICLE_COL_WIDTH, flexShrink: 0 }}>
        {/* Header spacer (day names row) */}
        <div style={{ height: 28, borderRight: "1px solid #D9D9D9", borderBottom: "1px solid #D9D9D9" }} />
        {/* Header spacer (counts row) */}
        <div style={{ height: 28, borderRight: "1px solid #D9D9D9", borderBottom: "1px solid #D9D9D9" }} />
        {/* Vehicle labels */}
        {vehicles.map((vehicle) => (
          <div
            key={vehicle.id}
            className="flex flex-col justify-center"
            style={{
              height: ROW_HEIGHT,
              padding: "0 4px",
              borderRight: "1px solid #D9D9D9",
              borderBottom: "1px solid #D9D9D9",
            }}
          >
            <span style={{
              fontFamily: "Poppins",
              fontWeight: 500,
              fontSize: 12,
              lineHeight: "120%",
              color: "#A1A1A1",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
            }}>
              {vehicle.name} {vehicle.plate} / {vehicle.km} KM
            </span>
          </div>
        ))}
      </div>

      {/* Right: day columns area (relative for today line) */}
      <div className="flex-1 relative" style={{ minWidth: 0 }}>
        {/* Today red line — spans full height */}
        {todayPct >= 0 && (
          <div
            className="absolute pointer-events-none"
            style={{
              top: 0,
              bottom: 0,
              left: `${todayPct}%`,
              width: 2,
              background: "#FCA5A5",
              zIndex: 25,
            }}
          />
        )}

        {/* Header row: day names */}
        <div className="flex flex-row" style={{ height: 28 }}>
          {weekDays.map((day) => (
            <div
              key={day.date}
              className="flex-1 flex items-center justify-center"
              style={{
                height: 28,
                borderRight: "1px solid #D9D9D9",
                borderBottom: "1px solid #D9D9D9",
                background: day.isWeekend ? "#E6E6E6" : undefined,
                fontFamily: "Poppins",
                fontWeight: 500,
                fontSize: 12,
                color: "#A1A1A1",
              }}
            >
              {day.label}
            </div>
          ))}
        </div>

        {/* Header row: counts */}
        <div className="flex flex-row" style={{ height: 28 }}>
          {weekDays.map((day) => {
            const c = dayCounts[day.date] || { departures: 0, arrivals: 0 };
            return (
              <div
                key={day.date}
                className="flex-1 flex flex-row items-center justify-between"
                style={{
                  height: 28,
                  padding: "5px 10px",
                  borderRight: "1px solid #D9D9D9",
                  borderBottom: "1px solid #D9D9D9",
                  background: day.isWeekend ? "#E6E6E6" : undefined,
                }}
              >
                <div className="flex flex-row items-center" style={{ gap: 5 }}>
                  <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 12, color: "#A1A1A1" }}>
                    {c.departures}
                  </span>
                  <Icon1 name="car-reception" size={14} color="#A1A1A1" />
                </div>
                <div className="flex flex-row items-center" style={{ gap: 5 }}>
                  <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 12, color: "#A1A1A1" }}>
                    {c.arrivals}
                  </span>
                  <Icon1 name="car-restitution" size={14} color="#A1A1A1" />
                </div>
              </div>
            );
          })}
        </div>

        {/* Vehicle rows */}
        {vehicles.map((vehicle) => {
          const vReservations = resByVehicle[vehicle.id] || [];
          return (
            <div key={vehicle.id} className="flex flex-row relative" style={{ height: ROW_HEIGHT }}>
              {/* Day background cells */}
              {weekDays.map((day) => (
                <div
                  key={day.date}
                  className="flex-1"
                  style={{
                    height: ROW_HEIGHT,
                    borderRight: "1px solid #D9D9D9",
                    borderBottom: "1px solid #D9D9D9",
                    background: day.isWeekend ? "#E6E6E6" : undefined,
                  }}
                />
              ))}

              {/* Reservation bars */}
              {vReservations.map((r) => {
                const barStyle = getBarStyle(r);
                const isHighlighted = highlightedId === r.id;
                return (
                  <div
                    key={r.id}
                    data-reservation-id={r.id}
                    className={`absolute flex flex-row items-stretch${isHighlighted ? " animate-pulse" : ""}`}
                    style={{
                      top: 0,
                      bottom: 0,
                      left: barStyle.left,
                      width: barStyle.width,
                      background: isHighlighted ? "#A5B4FC" : "#CACFFF",
                      border: isHighlighted ? "2px solid #6366f1" : "1px solid #B7BDFF",
                      boxShadow: isHighlighted ? "0px 0px 12px rgba(99, 102, 241, 0.5)" : "0px 2px 4px rgba(0, 0, 0, 0.15)",
                      borderRadius: 5,
                      zIndex: isHighlighted ? 20 : 10,
                      overflow: "hidden",
                    }}
                  >
                    {r.hasPickup && (
                      <div className="flex flex-col items-center justify-center" style={{ width: 20, flexShrink: 0, background: "#DFE2FF" }}>
                        <Icon1 name="car-restitution" size={14} color="#5C6BFF" />
                      </div>
                    )}
                    <div className="flex flex-row items-center flex-1" style={{ padding: "3px 3px", minWidth: 0, overflow: "hidden" }}>
                      <span style={{
                        fontFamily: "Poppins",
                        fontWeight: 500,
                        fontSize: 12,
                        lineHeight: "100%",
                        color: "#5C6BFF",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        flex: 1,
                      }}>
                        {r.clientName}
                      </span>
                    </div>
                    {r.hasReturn && (
                      <div className="flex flex-col items-center justify-center" style={{ width: 20, flexShrink: 0, background: "#BFC5FF" }}>
                        <Icon1 name="car-reception" size={14} color="#5C6BFF" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// --- Main Screen ---

const FUEL_TABS = [
  { value: "tous", label: "Tous" },
  { value: "diesel", label: "Diesel" },
  { value: "essence", label: "Essence" },
  { value: "electrique", label: "Electrique" },
  { value: "gpl", label: "GPL" },
  { value: "hybride", label: "Hybride" },
  { value: "autre", label: "Autres" },
];

export default function VehiculesScreen() {
  const orgData = useDataStore((s) => s.orgData);
  const fetchVehicles = useVehiculesStore((s) => s.fetchVehicles);
  const fetchReservations = useVehiculesStore((s) => s.fetchReservations);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (orgData?.id) {
      fetchVehicles(orgData.id);
      fetchReservations(orgData.id);
    }
  }, [orgData?.id]);

  // Handle ?reservation= query param: navigate to the reservation's date and highlight it
  useEffect(() => {
    const resId = searchParams.get("reservation");
    if (!resId) return;
    const { reservations, setSelectedDate, setViewMode, setTimeView, setHighlightedReservation } = useVehiculesStore.getState();
    const res = reservations.find((r) => r.id === resId);
    if (res) {
      setSelectedDate(res.startDate);
      setViewMode("reservations");
      setTimeView("jour");
      setHighlightedReservation(resId);
      // Clear highlight after 4 seconds
      setTimeout(() => setHighlightedReservation(null), 4000);
    }
    // Remove from URL
    searchParams.delete("reservation");
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, useVehiculesStore.getState().reservations.length]);

  const selectedDate = useVehiculesStore((s) => s.selectedDate);
  const setSelectedDate = useVehiculesStore((s) => s.setSelectedDate);
  const viewMode = useVehiculesStore((s) => s.viewMode);
  const setViewMode = useVehiculesStore((s) => s.setViewMode);
  const searchQuery = useVehiculesStore((s) => s.searchQuery);
  const setSearchQuery = useVehiculesStore((s) => s.setSearchQuery);
  const timeView = useVehiculesStore((s) => s.timeView);
  const setTimeView = useVehiculesStore((s) => s.setTimeView);
  const activeCategories = useVehiculesStore((s) => s.activeCategories);
  const activeUsages = useVehiculesStore((s) => s.activeUsages);
  const toggleCategory = useVehiculesStore((s) => s.toggleCategory);
  const toggleUsage = useVehiculesStore((s) => s.toggleUsage);
  const vehicles = useVehiculesStore((s) => s.vehicles);
  const reservations = useVehiculesStore((s) => s.reservations);
  const parcFuelTab = useVehiculesStore((s) => s.parcFuelTab);
  const setParcFuelTab = useVehiculesStore((s) => s.setParcFuelTab);
  const updateVehicle = useVehiculesStore((s) => s.updateVehicle);
  const createVehicle = useVehiculesStore((s) => s.createVehicle);

  const [parcTable, setParcTable] = useState<any>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [editingVehicle, setEditingVehicle] = useState<Vehicle | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    const table = transformVehiclesToTable(vehicles, parcFuelTab, reservations);
    setParcTable(table);
  }, [vehicles, reservations, parcFuelTab]);

  const handleVehicleClick = (row: any) => {
    const v = vehicles.find((v) => v.id === row.id);
    if (v) setEditingVehicle(v);
  };

  const handleVehicleSave = (updated: Vehicle) => {
    updateVehicle(updated.id, updated);
    const table = transformVehiclesToTable(
      vehicles.map((v) => (v.id === updated.id ? updated : v)),
      parcFuelTab,
      reservations,
    );
    setParcTable(table);
  };

  const handleCreateVehicle = async (vehicle: Vehicle) => {
    if (!orgData?.id) return;
    const { id, ...rest } = vehicle;
    await createVehicle({ ...rest, org_id: orgData.id } as any);
    const table = transformVehiclesToTable(useVehiculesStore.getState().vehicles, parcFuelTab, useVehiculesStore.getState().reservations);
    setParcTable(table);
  };

  return (
    <div className="flex flex-col max-h-screen overflow-auto" style={{ background: "#FAFAFA" }}>
      <div className="flex flex-col h-fit">
      {editingVehicle && (
        <VehiculeModal
          vehicle={editingVehicle}
          onClose={() => setEditingVehicle(null)}
          onSave={handleVehicleSave}
        />
      )}
      {showCreateModal && (
        <VehiculeModal
          onClose={() => setShowCreateModal(false)}
          onSave={handleCreateVehicle}
        />
      )}
      {/* Title */}
      <div className="flex flex-col px-4 lg:px-10 pt-8">
        <Text1>Gestion des véhicules</Text1>
        <Text2>Gérez les véhicules de prêt et de location ici.</Text2>
      </div>

      {/* Tabs + action */}
      <div className="flex flex-row items-center justify-between px-4 lg:px-10 pt-5">
        <ChunkyTabs
          activeTab={viewMode}
          tabs={[
            { value: "reservations", label: "Réservations" },
            { value: "parc", label: "Parc" },
          ]}
          setActiveTab={(tab) => setViewMode(tab as "reservations" | "parc")}
        />
        <Button label="Nouveau véhicule" onClick={() => setShowCreateModal(true)} className="whitespace-nowrap" />
      </div>

      {viewMode === "reservations" && (
        <div className="flex flex-row px-4 lg:px-10 pt-5 pb-8" style={{ gap: 30 }}>
          {/* Left sidebar */}
          <div className="flex flex-col" style={{ width: 196, flexShrink: 0, gap: 20 }}>
            <MiniCalendar selectedDate={selectedDate} onSelectDate={setSelectedDate} />

            {/* Category filters */}
            <div className="flex flex-col" style={{ gap: 5, padding: "5px 0", borderRadius: 5 }}>
              {CATEGORY_FILTERS.map((f) => (
                <div key={f.id} className="flex flex-row items-center" style={{ padding: "0 10px", gap: 10, height: 18 }}>
                  <CheckBox
                    checked={activeCategories.includes(f.id)}
                    onChange={() => toggleCategory(f.id)}
                  />
                  <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 12, lineHeight: "18px", color: "#A1A1A1", flex: 1 }}>
                    {f.label}
                  </span>
                </div>
              ))}
            </div>

            {/* Usage filters */}
            <div className="flex flex-col" style={{ gap: 5, padding: "5px 0", borderRadius: 5 }}>
              {USAGE_FILTERS.map((f) => (
                <div key={f.id} className="flex flex-row items-center" style={{ padding: "0 10px", gap: 10, height: 18 }}>
                  <CheckBox
                    checked={activeUsages.includes(f.id)}
                    onChange={() => toggleUsage(f.id)}
                  />
                  <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 12, lineHeight: "18px", color: "#A1A1A1", flex: 1 }}>
                    {f.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Right content */}
          <div className="flex flex-col flex-1" style={{ gap: 10, minWidth: 0 }}>
            {/* Search bar + time view toggle */}
            <div className="flex flex-row items-center" style={{ gap: 12 }}>
              <div style={{ width: 331 }}>
                <SearchField
                  value={searchQuery}
                  onChange={(v: string) => setSearchQuery(v)}
                  placeholder="Chercher .."
                />
              </div>
              <ChunkyTabs
                activeTab={timeView}
                tabs={[
                  { value: "semaine", label: "Semaine" },
                  { value: "jour", label: "Jour" },
                ]}
                setActiveTab={(tab) => setTimeView(tab as "semaine" | "jour")}
              />
            </div>

            {timeView === "semaine" && <ReservationGrid />}
            {timeView === "jour" && <DayReservationGrid />}
          </div>
        </div>
      )}

      {viewMode === "parc" && (
        <div className="px-4 lg:px-10 py-5 flex flex-col gap-3 w-full">
          <div className="max-w-1p2k w-full">
            {parcTable && (
              <DataGrid
                selectable={true}
                table={parcTable}
                hide_lines={true}
                tabs={FUEL_TABS}
                activeTab={parcFuelTab}
                onTabChange={(tab) => {
                  setParcFuelTab(tab);
                  const table = transformVehiclesToTable(vehicles, tab, reservations);
                  setParcTable(table);
                }}
                onSearch={async (query) => {
                  if (!orgData?.id) return [];
                  return searchVehiclesFull(query, orgData.id);
                }}
                onSearchSelect={(option) => {
                  const v = vehicles.find((v) => v.id === option.value);
                  if (v) setEditingVehicle(v);
                }}
                onClick={handleVehicleClick}
                handleSelectionModelChange={(ids) => setSelectedIds(ids)}
              />
            )}
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
